import React, { useCallback, useEffect, useState } from 'react';
import { Button, Container, IconButton, SwipeableDrawer, Tooltip, Chip, Box, TextField } from '@mui/material';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import { fCurrency } from '../../../utils/formatNumber';
import {
  getPurchaseNotes,
  updatePurchaseNotes,
  updatePurchaseNotesPaymentNote,
  uploadPurchaseNotesDocuments,
  sendPurchaseNoteSMS,
  updateSupplierInvoiceNumber,
} from '../../../redux/slices/purchaseRedux/purchaseNotesRedux';
import { BASE_URL, COMPANY_DATA_FOR_PDF } from '../../../config';
import { accessVerify, capitalize, handleNumber, removeUnwantedStockBarCodeList, safeJSONParse, utcMoment } from '../../../utils/common';
import PermissionRequired from '../../errorPages/permissionRequired';
import { EditNote } from '@mui/icons-material';
import { CloudUploadRounded, PictureAsPdf } from '@material-ui/icons';
import CashFlowImageUploader, { isImageDocument } from '../finance/cashflow/components/cashflowImageUploader';
import ImageViewer from 'react-simple-image-viewer';
import { imageUpload } from '../../../inteceptor';
import Iconify from '../../../components/Iconify';
import PdfViewerDrawer from '../../../components/pdfViewerDrawer';
import { toast } from 'react-toastify';

export default function PurchaseNotes() {
  const { themeStretch } = useSettings();
  const [enteredPricing, setEnteredPricing] = useState([]);
  const [rowSelection, setRowSelection] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [documentGallery, setDocumentGallery] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);
  const [selectedDataObj, setSelectedDataObj] = useState({ linkedDocuments: [] });
  const [isPdfDrawerOpen, setIsPdfDrawerOpen] = useState(false);
  const [pdfRow, setPdfRow] = useState(null);
  const [smsSendingId, setSmsSendingId] = useState(null);
  const [supplierInvoiceCode, setSupplierInvoiceCode] = useState('');
  const [workflowLog, setWorkflowLog] = useState(null);
  const imageGallery = documentGallery?.filter(isImageDocument) || [];

  const { loggedInUser } = useSelector((state) => state?.user);
  const { data, addData } = useSelector((state) => state?.purchaseNotes);
  const { selectedLocation } = useSelector((state) => state.location);

  useEffect(() => {
    dispatch(getPurchaseNotes({ locationId: selectedLocation.id }));
  }, [selectedLocation, addData]);

  useEffect(() => {
    setEnteredPricing(
      data?.map((value) => {
        return {
          day: utcMoment(value?.date).format('YYYY-MM-DD'),
          time: utcMoment(value?.date).format('hh:mm A'),
          ...value,
        };
      })
    );
    setEnteredPricing(
      data?.map((value) => {
        return {
          ...value,
          linkedDocuments: value?.linkedDocuments ? safeJSONParse(value?.linkedDocuments)?.map((value) => value?.url) : [],
        };
      })
    );
  }, [data]);

  useEffect(() => {
    if (addData?.data) {
      dispatch(getPurchaseNotes({ locationId: selectedLocation.id }));
    }
  }, [addData]);

  const getItemsForInvoice = (rowData) => {
    if (rowData) {
      const parsedRowData = safeJSONParse(rowData);
      return parsedRowData?.map((value, index) => {
        const jsonData = safeJSONParse(value?.jsonData);
        const pricePerSupplierUnit = (jsonData?.price / jsonData?.purchaseUnitQty) * jsonData?.storeUnitQty;
        return {
          id: index,
          title: jsonData?.FoodName,
          foodId: value?.foodId,
          description: `Final Price is checked on ${jsonData?.formatted_dateTime}.`,
          quantity: jsonData?.purchaseUnitQty,
          storeUnitQty: jsonData?.storeUnitQty + ' ' + jsonData?.unitSymbol,
          price: pricePerSupplierUnit,
        };
      });
    }
  };
  useEffect(() => {
    setDocumentGallery(selectedDataObj?.linkedDocuments);
  }, [selectedDataObj]);

  useEffect(() => {
    setSupplierInvoiceCode(selectedDataObj?.supplierInvoiceNumber || '');
  }, [selectedDataObj?.id]);

  const parseWorkflowLogs = (rowData) => {
    const rawLog = rowData?.workflowLog;
    if (!rawLog) return [];

    const parsedLog = safeJSONParse(rawLog);
    if (!Array.isArray(parsedLog)) return [];

    return parsedLog.map((log) => ({
      ...log,
      date: log?.date ? utcMoment(log.date).format('YYYY-MM-DD HH:mm:ss') : '',
    }));
  };

  const handleDownload = async (row) => {
    setIsPdfDrawerOpen(true);
    setWorkflowLog(parseWorkflowLogs(row?.original));
    setPdfRow({
      invoiceNumber: `INV-${row?.original?.id}`,
      createDate: utcMoment(row?.original?.date).format('DD MMMM yyyy').toString(),
      dueDate: utcMoment(row?.original?.date)
        .add(row?.original?.supplierNumberOfDaysForPayment || 30, 'days')
        .format('DD MMMM yyyy')
        .toString(),
      status: row?.original?.status,
      discount: 0,
      taxes: 0,
      paidAmount: handleNumber(row?.original?.paidamount),
      totalPrice: handleNumber(row?.original?.totalamount) - handleNumber(row?.original?.paidamount),
      subTotalPrice: handleNumber(row?.original?.totalamount),
      topic: 'Purchase Order',
      invoiceTo: {
        name: COMPANY_DATA_FOR_PDF?.name,
        address: row?.original?.locationAddress,
        phone: row?.original?.locationContactNumber,
      },
      invoiceFrom: {
        name: row?.original?.supplierName,
        address: row?.original?.supplierAddress,
        phone: row?.original?.supplierCompanyPhone,
      },
      items: getItemsForInvoice(row?.original?.supplierorder_foodsupplierpricing),
      stockCodes: removeUnwantedStockBarCodeList(safeJSONParse(row?.original?.stockCodes)) || [],
      linkedDocuments: row?.original?.linkedDocuments?.map((value) => value?.replace('https://storage.googleapis.com/bubblemania/', `${BASE_URL}imageViewer?path=`)) || [],
      supplierInvoiceNumber: row?.original?.supplierInvoiceNumber,
    });
  };

  const StatusChip = ({ label, color, variant = 'filled' }) => (
    <Chip
      size='small'
      label={label}
      color={color}
      variant={variant}
      sx={{
        width: 90, // 👈 fixed width
        justifyContent: 'center',
        fontWeight: 500,
      }}
    />
  );

  const columns = !enteredPricing?.[0]
    ? []
    : Object.keys(enteredPricing?.[0])
        .map((value) => {
          if (
            value === 'date' ||
            value === 'supplierAddress' ||
            value === 'estimatedreceivingdate' ||
            value === 'paymentdate' ||
            value === 'paymentmethod' ||
            value === 'supplierCode' ||
            value === 'supplierCompanyPhone' ||
            value === 'paymentMethod' ||
            value === 'paymentDate' ||
            value === 'estimatedReceivingDate' ||
            value === 'orderedById' ||
            value === 'isSupplierInvoiceNumberEditable' ||
            value === 'supplierorder_foodsupplierpricing'
          ) {
            return null;
          } else if (value === 'supplierId') {
            return {
              accessorKey: 'supplierId',
              header: 'Invoice Id',
              Cell: ({ row }) => `INV-${row?.original?.id}`,
            };
          } else if (value === 'totalamount') {
            return {
              accessorKey: 'totalamount',
              header: 'Total Amount',
              Cell: ({ row }) => fCurrency(row?.original?.totalamount),
            };
          } else if (value === 'paidamount') {
            return {
              accessorKey: 'paidamount',
              header: 'Paid Amount',
              Cell: ({ row }) => fCurrency(row?.original?.paidamount),
            };
          } else if (value === 'paymentforreceivings') {
            return {
              accessorKey: 'paymentforreceivings',
              header: 'Payment Amount For Receivings',
              Cell: ({ row }) => fCurrency(row?.original?.paymentforreceivings),
            };
          } else if (value === 'smsStatus') {
            return {
              accessorKey: 'smsStatus',
              header: 'SMS Status',
              Cell: ({ row }) => {
                const status = row.original.smsStatus;

                if (status === 'VIEWED') {
                  return <StatusChip label='Viewed' color='success' />;
                }

                if (status === 'SENT') {
                  return <StatusChip label='Sent' color='info' />;
                }

                return <StatusChip label='Not Sent' variant='outlined' />;
              },
            };
          } else {
            return {
              accessorKey: value,
              header: capitalize(value),
            };
          }
        })
        .filter((value) => value);

  const uploadFiles = (file) => {
    return new Promise((resolve, reject) => {
      imageUpload(
        file,
        `Finance/PurchaseNote_Invoice/Loc_${selectedLocation?.id}/User_${loggedInUser?.id}`,
        (data) => {
          if (data?.status) {
            resolve(data);
          } else {
            reject(new Error('Upload failed'));
          }
        },
        (errorMessage) => {
          console.error('Upload failed:', errorMessage);
          reject(new Error(errorMessage));
        }
      );
    });
  };

  const handleImageUpload = async () => {
    setIsUploading(true);
    for (const file of files) {
      try {
        setFiles((value) => {
          return value.map((fileValue, index) => {
            if (file?.id === fileValue?.id) {
              return {
                ...fileValue,
                isUploading: true,
              };
            }
            return fileValue;
          });
        });

        const data = await uploadFiles(file);

        setFiles((value) => {
          return value.filter((fileValue, index) => file?.id !== fileValue?.id);
        });
        if (data?.status) {
          setSelectedDataObj((prevState) => ({
            ...prevState,
            linkedDocuments: [...prevState.linkedDocuments, data?.url],
          }));
          dispatch(uploadPurchaseNotesDocuments({ supplierorderId: selectedDataObj?.id, url: data?.url }));
        }
      } catch (error) {
        setFiles((value) => {
          return value.map((fileValue, index) => {
            if (file?.id === fileValue?.id) {
              return {
                ...fileValue,
                isUploading: false,
              };
            }
            return fileValue;
          });
        });
      }
    }
    setIsUploading(false);
  };

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  return (
    <Page title='Purchase Notes'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading='Purchase Notes'
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            {
              name: 'Purchasing',
              href: PATH_DASHBOARD.purchasing.root,
            },
            { name: 'Purchase Notes' },
          ]}
          action={[]}
        />

        {accessVerify('PURCHASE_NOTE_VIEW') ? (
          <DataGridTable
            name={'Purchase Notes'}
            data={enteredPricing}
            column={columns}
            isLoading={false}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowActions={true}
            renderRowActionItems={(row, closeMenu) => [
              <div style={{ minWidth: '100px' }}>
                <Tooltip title='Add Payment Note'>
                  <IconButton
                    color='secondary'
                    onClick={() => {
                      const paymentNote = prompt('Enter Purchase Note:', row.original?.paymentNote || '');
                      if (paymentNote) {
                        dispatch(updatePurchaseNotesPaymentNote({ id: row.original?.id, paymentNote }));
                      }
                    }}
                  >
                    <EditNote />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Download PDF'>
                  <IconButton
                    color='error'
                    onClick={() => {
                      handleDownload(row);
                    }}
                  >
                    <PictureAsPdf />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Upload Invoice'>
                  <IconButton
                    color={row.original?.status === 'DRAFT' ? 'warning' : 'success'}
                    onClick={() => {
                      setIsModelOpen(true);
                      setSelectedDataObj(row?.original);
                      setSupplierInvoiceCode(row.original?.supplierInvoiceNumber || '');
                    }}
                  >
                    <CloudUploadRounded />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Send SMS'>
                  <IconButton
                    color='primary'
                    disabled={smsSendingId === row.original.id || !row.original?.supplierCompanyPhone}
                    onClick={async () => {
                      setSmsSendingId(row.original.id);
                      try {
                        await dispatch(sendPurchaseNoteSMS({ id: row.original.id }));
                      } finally {
                        setSmsSendingId(null);
                      }
                    }}
                  >
                    <Iconify icon='eva:message-circle-fill' />
                  </IconButton>
                </Tooltip>
              </div>,
            ]}
          />
        ) : (
          <PermissionRequired />
        )}
        <SwipeableDrawer
          anchor={'bottom'}
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => setIsModelOpen(false)}
          PaperProps={{
            sx: {
              width: '95%',
              height: '95%',
              marginLeft: '2.5%',
              borderRadius: '10px',
            },
          }}
        >
          <Box sx={{ px: 2, pt: 2, mt: 3 }}>
            <TextField
              fullWidth
              label='Supplier Invoice Number'
              placeholder={'Enter Supplier Invoice Number '}
              value={supplierInvoiceCode}
              required={selectedDataObj?.isSupplierInvoiceNumberEditable === 0}
              disabled={selectedDataObj.status !== 'DRAFT' && selectedDataObj.isSupplierInvoiceNumberEditable == 0}
              onChange={(e) => setSupplierInvoiceCode(e.target.value)}
            />
          </Box>

          {selectedDataObj.status !== 'DRAFT' && selectedDataObj.isSupplierInvoiceNumberEditable == 1 && selectedDataObj.supplierInvoiceNumber !== supplierInvoiceCode && (
            <Button
              sx={{ mx: 2, mt: 2 }}
              variant='outlined'
              onClick={() => {
                if (!supplierInvoiceCode) {
                  toast.error('Supplier Invoice Number is required');
                  return;
                }

                dispatch(
                  updateSupplierInvoiceNumber({
                    id: selectedDataObj?.id,
                    supplierInvoiceNumber: supplierInvoiceCode,
                  })
                );
                setIsModelOpen(false);
              }}
            >
              Save Supplier Invoice Number
            </Button>
          )}

          <CashFlowImageUploader
            files={files}
            setFiles={setFiles}
            handleImageUpload={handleImageUpload}
            documentGallery={documentGallery}
            openImageViewer={openImageViewer}
            isUploading={isUploading}
          />
          {isViewerOpen && <ImageViewer src={imageGallery} currentIndex={currentImage} disableScroll={false} closeOnClickOutside={true} onClose={closeImageViewer} />}

          {selectedDataObj?.linkedDocuments?.length > 0 && selectedDataObj?.status === 'DRAFT' && (
            <Button
              sx={{ mx: 2 }}
              variant='contained'
              startIcon={<Iconify icon='eva:plus-fill' />}
              onClick={() => {
                if (!supplierInvoiceCode) {
                  toast.error('Supplier Invoice Number is required');
                  return;
                }
                setSelectedDataObj((prevState) => {
                  return { ...prevState, status: 'Updated' };
                });

                dispatch(
                  updatePurchaseNotes({
                    id: selectedDataObj?.id,
                    status: 'PROCESSING',
                    supplierInvoiceNumber: supplierInvoiceCode || null,
                  })
                );
              }}
            >
              Upload And continue process
            </Button>
          )}
        </SwipeableDrawer>
        <PdfViewerDrawer open={isPdfDrawerOpen} onClose={() => setIsPdfDrawerOpen(false)} invoice={pdfRow} workflowLog={workflowLog} />
      </Container>
    </Page>
  );
}
